import { DropDown } from '../../models/api/dropDown';
import {
    BaseSyntheticEvent,
    createContext,
    PropsWithChildren,
    RefObject,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    DeepRequired,
    DefaultValues,
    FieldErrorsImpl,
    FieldValues,
    FormProvider,
    useForm,
    UseFormProps,
    UseFormReturn,
} from 'react-hook-form';
import { GroupBase } from 'react-select';
import Select from 'react-select/dist/declarations/src/Select';
import './BaseForm.scss';

export type FocusFieldType =
    | HTMLInputElement
    | HTMLTextAreaElement
    | Select<DropDown, false, GroupBase<DropDown>>
    | null;

type FormFocusContextType = {
    isFocusing: boolean;
    focusFieldRef: FocusFieldType;
    setFocusFieldRef: (value: FocusFieldType) => void;
};

const FormFocusContext = createContext<FormFocusContextType>({} as FormFocusContextType);

const BaseForm = <T extends FieldValues>({
    innerRef,
    onSubmit,
    onInvalid,
    defaultValues,
    className,
    children,
    formProps,
    clearAfterSubmit = true,
    getMethods,
    testId,
}: PropsWithChildren<{
    innerRef?: RefObject<HTMLFormElement>;
    onSubmit?: (data: T, event: BaseSyntheticEvent<object, any, any> | undefined) => void;
    onInvalid?: (
        error: Partial<FieldErrorsImpl<DeepRequired<T>>>,
        event: BaseSyntheticEvent<object, any, any> | undefined
    ) => void;
    defaultValues?: DefaultValues<T>;
    className?: string;
    formProps?: Omit<UseFormProps<T>, 'defaultValues'>;
    clearAfterSubmit?: boolean;
    getMethods?: (m: UseFormReturn<T, any>) => void;
    testId?: string;
}>) => {
    const [isFocusing, setFocusing] = useState(false);
    const [focusFieldRef, setFocusFieldRef] = useState<FocusFieldType>(null);

    const setRef = (value: FocusFieldType) => {
        setFocusing(true);
        setFocusFieldRef(value);
        setTimeout(() => setFocusing(false), 1);
    };

    const methods = useForm<T>({
        defaultValues: useMemo(() => defaultValues, [defaultValues]),
        ...formProps,
    });

    useEffect(() => {
        getMethods?.(methods);
    }, [methods, getMethods]);

    useEffect(
        () =>
            methods.reset(defaultValues, {
                keepDefaultValues: true,
            }),
        [defaultValues]
    );

    return (
        <FormFocusContext.Provider
            value={{
                focusFieldRef,
                isFocusing,
                setFocusFieldRef: setRef,
            }}
        >
            <FormProvider {...methods}>
                <form
                    aria-label="form"
                    ref={innerRef}
                    onSubmit={
                        onSubmit &&
                        methods.handleSubmit(
                            (data, e) => {
                                onSubmit(data, e);
                                clearAfterSubmit && methods.reset(defaultValues);
                            },
                            (errors, event) => onInvalid && onInvalid(errors, event)
                        )
                    }
                    className={className}
                    data-testid={testId}
                >
                    {children}
                </form>
            </FormProvider>
        </FormFocusContext.Provider>
    );
};
const useFormFocusState = () => {
    const context = useContext(FormFocusContext);

    return context;
};

export { useFormFocusState };

export default BaseForm;
